import { Component, computed, inject, input, output, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { TranslocoModule } from "@jsverse/transloco";

export interface TourStep {
  title: string;
  description: string;
  icon: string;
  accentGradient: string;
  orbGradient: string;
  svgPath: string;
}

@Component({
  selector: "app-page-tour",
  imports: [TranslocoModule],
  templateUrl: "./page-tour.component.html",
  styleUrls: ["./page-tour.component.scss"],
  host: {
    "(document:keydown)": "onKeydown($event)",
  },
})
export class PageTourComponent {
  private _router = inject(Router);

  readonly steps = input<TourStep[]>([]);
  readonly storageKey = input("");
  readonly routePrefix = input("");
  // i18n-ignore — default never rendered; every call site passes a translated label
  readonly ariaLabel = input("Page tour");
  readonly tourComplete = output<void>();

  readonly isVisible = signal(false);
  readonly currentStep = signal(0);
  readonly direction = signal<"forward" | "back">("forward");
  private readonly _isTransitioning = signal(false);
  private _showScheduled = false;

  readonly step = computed(() => this.steps()[this.currentStep()]);
  readonly isFirstStep = computed(() => this.currentStep() === 0);
  readonly isLastStep = computed(() => this.currentStep() === this.steps().length - 1);
  readonly progressPercent = computed(() => ((this.currentStep() + 1) / this.steps().length) * 100);

  constructor() {
    this._router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => this._checkRoute(e.urlAfterRedirects));
  }

  ngOnInit(): void {
    this._checkRoute(this._router.url);
  }

  private _checkRoute(url: string): void {
    if (!url.startsWith(this.routePrefix())) return;
    if (this._showScheduled || this.isVisible()) return;
    if (localStorage.getItem(this.storageKey())) return;
    this._showScheduled = true;
    requestAnimationFrame(() => {
      this.isVisible.set(true);
      this._showScheduled = false;
      document.body.style.overflow = "hidden";
    });
  }

  next(): void {
    if (this._isTransitioning()) return;
    if (this.isLastStep()) {
      this.complete();
      return;
    }
    this.goTo(this.currentStep() + 1);
  }

  back(): void {
    if (this._isTransitioning() || this.isFirstStep()) return;
    this.goTo(this.currentStep() - 1);
  }

  goTo(index: number): void {
    if (index === this.currentStep()) return;
    this.direction.set(index > this.currentStep() ? "forward" : "back");
    this._isTransitioning.set(true);
    this.currentStep.set(index);
    setTimeout(() => this._isTransitioning.set(false), 420);
  }

  skip(): void {
    this.complete();
  }

  complete(): void {
    localStorage.setItem(this.storageKey(), "true");
    this.isVisible.set(false);
    document.body.style.overflow = "";
    this.tourComplete.emit();
  }

  onKeydown(event: KeyboardEvent): void {
    if (!this.isVisible()) return;
    if (event.key === "ArrowRight" || event.key === "Enter") this.next();
    else if (event.key === "ArrowLeft") this.back();
  }
}
